import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type Updater,
} from "@tanstack/react-table";
import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "./button";
import { Card } from "./card";
import { formatArabicNumber } from "@/lib/formatters";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { cn } from "@/lib/utils";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Server/manual pagination. When provided, the table stops slicing client-side
   *  and its pager drives the server via `onPageChange` (0-based). */
  manualPagination?: {
    pageIndex: number;
    pageCount: number;
    onPageChange: (pageIndex: number) => void;
    total?: number;
    pageSize?: number;
  };
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  manualPagination,
  className,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    if (!manualPagination) return;
    const current: PaginationState = {
      pageIndex: manualPagination.pageIndex,
      pageSize: data.length || 1,
    };
    const next =
      typeof updater === "function" ? updater(current) : updater;
    if (next.pageIndex !== current.pageIndex) {
      manualPagination.onPageChange(next.pageIndex);
    }
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      ...(manualPagination
        ? {
            pagination: {
              pageIndex: manualPagination.pageIndex,
              pageSize: data.length || 1,
            },
          }
        : {}),
    },
    onSortingChange: setSorting,
    ...(manualPagination
      ? {
          manualPagination: true,
          pageCount: manualPagination.pageCount,
          onPaginationChange: handlePaginationChange,
        }
      : {}),
  });

  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = manualPagination?.pageSize ?? table.getState().pagination.pageSize;
  const total = manualPagination?.total ?? table.getPrePaginationRowModel().rows.length;
  const from = total === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, total);

  return (
    <Card className={cn("overflow-hidden", className)}>
      <Table>
        <TableHeader className="bg-muted/30">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const isSorted = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    className="cursor-pointer select-none text-center text-xs"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <span className="flex items-center justify-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {isSorted && (
                          <span className="text-primary text-[10px]">
                            {isSorted === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </span>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="hover:bg-muted/20">
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className="text-center text-sm whitespace-nowrap"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                لا توجد بيانات
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between border-t border-border bg-muted/20 px-4 py-3 text-sm">
        <p className="text-muted-foreground tabular-nums">
          {from}–{to} من {formatArabicNumber(total)}
        </p>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            aria-label="الصفحة الأولى"
          >
            <ChevronsRight className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            aria-label="الصفحة السابقة"
          >
            <ChevronRight className="size-4" />
          </Button>
          <span className="px-2 text-xs text-muted-foreground tabular-nums">
            {pageIndex + 1} / {table.getPageCount()}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="الصفحة التالية"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            aria-label="الصفحة الأخيرة"
          >
            <ChevronsLeft className="size-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

interface FilterOptions {
  category: Array<{ label: string; value: string }>;
  teacher: Array<{ label: string; value: string }>;
}

interface FiltersProps {
  options: FilterOptions;
  onFilter: (
    key: keyof FilterOptions,
    value: { label: string; value: string } | null,
  ) => void;
  onReset: () => void;
  isLoading: boolean;
  resultCount: number;
}

export function Filters({
  options,
  onFilter,
  onReset,
  isLoading,
  resultCount,
}: FiltersProps) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-muted/30 p-3">
      <select
        className="h-8 min-w-[150px] rounded-md border border-input bg-background px-2 text-xs"
        onChange={(e) => {
          const val = options.category.find((o) => o.value === e.target.value);
          onFilter("category", val ?? null);
        }}
        defaultValue=""
      >
        <option value="" disabled>
          اختر التصنيف
        </option>
        {options.category.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <select
        className="h-8 min-w-[150px] rounded-md border border-input bg-background px-2 text-xs"
        onChange={(e) => {
          const val = options.teacher.find((o) => o.value === e.target.value);
          onFilter("teacher", val ?? null);
        }}
        defaultValue=""
      >
        <option value="" disabled>
          اختر المدرب
        </option>
        {options.teacher.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <Button
        size="sm"
        className="h-8 px-3 text-xs"
        onClick={() => onFilter("category", null)}
        disabled={isLoading}
      >
        {isLoading ? "...جاري التصفية" : "تصفية"}
      </Button>

      <Button
        variant="outline"
        size="sm"
        className="h-8 px-2 text-xs"
        onClick={onReset}
        disabled={isLoading}
      >
        إعادة تعيين
      </Button>

      <span className="mr-2 text-xs text-muted-foreground">
        {formatArabicNumber(resultCount)} نتيجة
      </span>
    </div>
  );
}
